import { createTheme } from '@mui/material/styles';
import { Box, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import PropTypes from 'prop-types';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import GradientIcon from '@mui/icons-material/Gradient';

/**
 * Theme Definitions
 * ==================
 * Multiple theme variants for different user preferences:
 * - light: Clean white background with red accents
 * - dark: Dark background with bright accents
 * - gradient: Colorful gradient background with pink/purple
 */

const themeDefinitions = {
    light: {
        palette: {
            mode: 'light',
            primary: {
                main: '#D32F2F', // Red
                dark: '#B71C1C',
                light: '#EF5350',
            },
            secondary: {
                main: '#000000', // Black
                dark: '#424242',
                light: '#757575',
            },
            background: {
                default: '#ffffff', // White background
                paper: '#ffffff',
            },
            text: {
                primary: '#000000', // Black text
                secondary: '#424242',
            },
            error: {
                main: '#D32F2F',
            },
            success: {
                main: '#4CAF50',
            },
            warning: {
                main: '#FF9800',
            },
        },
    },
    dark: {
        palette: {
            mode: 'dark',
            primary: {
                main: '#D32F2F', // Brighter red for dark mode
                dark: '#B71C1C',
                light: '#FF8A80',
            },
            secondary: {
                main: '#ffffff', // White for contrast
                dark: '#E0E0E0',
                light: '#ffffff',
            },
            background: {
                default: '#121212', // Dark background
                paper: '#1E1E1E',
            },
            text: {
                primary: '#ffffff', // White text
                secondary: '#B0B0B0',
            },
            error: {
                main: '#FF5252',
            },
            success: {
                main: '#69F0AE',
            },
            warning: {
                main: '#FFB74D',
            },
        },
    },
    gradient: {
        palette: {
            mode: 'light',
            primary: {
                main: '#E91E63', // Pink
                dark: '#AD1457',
                light: '#F06292',
            },
            secondary: {
                main: '#FF5722', // Red-orange
                dark: '#D84315',
                light: '#FF8A65',
            },
            background: {
                default: '#FFE0E6', // Light pink background
                paper: '#FFFAFB',
            },
            text: {
                primary: '#2D1B69', // Dark purple text
                secondary: '#6A1B9A',
            },
            error: {
                main: '#E91E63',
            },
            success: {
                main: '#4CAF50',
            },
            warning: {
                main: '#FF9800',
            },
        },
    },
};

/**
 * Create app theme based on mode
 * @param {string} mode - Theme mode: 'light', 'dark', or 'gradient'
 * @returns {Theme} MUI theme object
 */
const createAppTheme = (mode) => {
    return createTheme({
        ...themeDefinitions[mode],
        typography: {
            fontFamily: 'Roboto, sans-serif',
            h4: {
                fontWeight: 500,
            },
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        textTransform: 'none',
                        transition: 'all 0.3s ease',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        transition: 'all 0.3s ease',
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        transition: 'all 0.3s ease',
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderWidth: '1px',
                            },
                            '&:hover fieldset': {
                                borderWidth: '1px',
                            },
                            '&.Mui-focused fieldset': {
                                borderWidth: '2px',
                                borderColor: 'primary.main', // Keeps MUI's styled border
                                boxShadow: 'none',
                            },
                        },
                    },
                },
            },
        },
    });
};

// =============================================
// THEME SWITCHER COMPONENT
// =============================================
const ThemeSwitcher = ({ currentTheme, onThemeChange }) => {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ mr: 1, fontWeight: 500 }}>
                Theme:
            </Typography>
            <ToggleButtonGroup
                value={currentTheme}
                exclusive
                onChange={(event, newTheme) => {
                    if (newTheme !== null) {
                        onThemeChange(newTheme);
                    }
                }}
                size="small"
                sx={{
                    '& .MuiToggleButton-root': {
                        px: 1.5,
                        py: 0.5,
                        border: 'none',
                        borderRadius: '8px !important',
                        mx: 0.5,
                        transition: 'all 0.3s ease',
                        '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                            },
                        },
                    },
                }}
            >
                <ToggleButton value="light" aria-label="light mode">
                    <LightModeIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="dark" aria-label="dark mode">
                    <DarkModeIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="gradient" aria-label="gradient mode">
                    <GradientIcon fontSize="small" />
                </ToggleButton>
            </ToggleButtonGroup>
        </Box>
    );
};
ThemeSwitcher.propTypes = {
    currentTheme: PropTypes.string.isRequired,
    onThemeChange: PropTypes.func.isRequired,
};

export { createAppTheme, ThemeSwitcher };